import { useMultiFieldSet } from "@/components/utils/useFieldSet";
import { NonNullish } from "@/flokit/utils/comparison/non-nullish";
import {
  JobAppSectionResponseSectionType,
  JobAppWorkHistoryRequest,
} from "modules/api-docs";
import {
  WorkHistoryFields,
  workHistoryYupSchema,
} from "modules/self-apply/validation/candidate-self-apply";
import { useMemo } from "react";
import {
  SelfApplyOnlyBaseJobAppSectionFieldSetArgs,
  SelfApplyOnlyNullishExistingJobApp,
  SelfApplyOnlyNullishJobSection,
  selfApplyOnlyBaseIsSectionRequired,
} from "./common";

export function isWorkHistoryRequired(
  jobSections: SelfApplyOnlyNullishJobSection[],
) {
  return selfApplyOnlyBaseIsSectionRequired(
    jobSections,
    JobAppSectionResponseSectionType.WORK_HISTORY,
  );
}

export function extractWorkHistoryFromExistingJobApp(args: {
  existingJobApp: SelfApplyOnlyNullishExistingJobApp;
}): WorkHistoryFields[] {
  const existingAnswers: WorkHistoryFields[] = [];
  if (!args.existingJobApp?.workHistory) {
    return [];
  }
  const existingWorkHistory =
    args.existingJobApp.workHistory.filter(NonNullish);

  for (const entry of existingWorkHistory) {
    const answer: WorkHistoryFields = {
      employer: entry.employer ?? null,
      position: entry.position ?? null,
      startDate: entry.startDate ?? null,
      endDate: entry.endDate ?? null,
      isCurrentEmployer: entry.isCurrentEmployer ?? false,
    };

    existingAnswers.push(answer);
  }

  return existingAnswers;
}

type UseWorkHistoryFieldSetArgs = SelfApplyOnlyBaseJobAppSectionFieldSetArgs;
export function useWorkHistoryFieldSet({
  jobSections,
  existingJobApp,
}: UseWorkHistoryFieldSetArgs) {
  const initialValue = useMemo(() => {
    return extractWorkHistoryFromExistingJobApp({ existingJobApp });
  }, [existingJobApp]);

  return useMultiFieldSet<WorkHistoryFields>(
    initialValue,
    [initialValue],
    workHistoryYupSchema,
    {
      minLength: isWorkHistoryRequired(jobSections) ? 1 : undefined,
    },
  );
}

export function workHistoryToRequests(args: { fields: WorkHistoryFields[] }) {
  return args.fields.map((entry) => {
    const req: JobAppWorkHistoryRequest = {
      employer: entry.employer ?? undefined,
      position: entry.position ?? undefined,
      startDate: entry.startDate ?? undefined,
      endDate: entry.endDate ?? undefined,
      isCurrentEmployer: !!entry.isCurrentEmployer,
      // TODO: This is not set in the self apply code; do we need it here?
      jobAppId: undefined,
    };

    return req;
  });
}
